import React, { useEffect, useState } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useTour } from '../context/TourContext';

export default function GuidedTour() {
  const {
    isActive,
    currentStep,
    tourSteps,
    hasHiddenTourPermanently,
    startTour,
    endTour,
    nextStep,
    prevStep,
    skipTour,
    hideTourPermanently
  } = useTour();

  const [dontShowAgain, setDontShowAgain] = useState(false);
  const currentStepData = tourSteps[currentStep];

  // Highlight element if target is specified
  useEffect(() => {
    if (isActive && currentStepData?.target) {
      const element = document.querySelector(currentStepData.target) as HTMLElement;
      if (element) {
        element.classList.add('tour-highlight');
        // Ensure element is above the overlay
        element.style.position = 'relative';
        element.style.zIndex = '45';
        return () => {
          element.classList.remove('tour-highlight');
          element.style.position = '';
          element.style.zIndex = '';
        };
      }
    }
  }, [isActive, currentStepData]);

  // Reset checkbox when tour becomes inactive
  useEffect(() => {
    if (!isActive) {
      setDontShowAgain(false);
    }
  }, [isActive]);

  // Don't show the tour button if user has hidden it permanently
  if (!isActive) {
    if (hasHiddenTourPermanently) {
      return null;
    }

    return (
      <button
        onClick={startTour}
        className="fixed bottom-6 left-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-full px-4 py-3 shadow-xl hover:shadow-2xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 hover:scale-110 z-40 flex items-center space-x-2 animate-pulse-subtle group"
        aria-label="Start guided tour"
        style={{
          boxShadow: '0 10px 30px rgba(147, 51, 234, 0.3)'
        }}
      >
        <PlayIcon className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
        <span className="text-sm font-semibold">Take a Tour</span>
      </button>
    );
  }

  // Determine positioning based on whether it's a general or specific feature tour
  const getModalPosition = () => {
    if (!currentStepData.target) {
      // General page description - position below navbar
      return 'top-20 right-4';
    }

    // Specific feature - position based on the step's position preference
    switch (currentStepData.position) {
      case 'top':
        return 'bottom-24 left-1/2 transform -translate-x-1/2';
      case 'bottom':
        return 'top-24 left-1/2 transform -translate-x-1/2';
      case 'left':
        return 'right-8 top-1/2 transform -translate-y-1/2';
      case 'right':
        return 'left-8 top-1/2 transform -translate-y-1/2';
      default:
        return 'top-20 right-4';
    }
  };

  // Add arrow/pointer styles based on position
  const getArrowStyles = () => {
    if (!currentStepData.target) return '';

    switch (currentStepData.position) {
      case 'top':
        return 'after:content-[""] after:absolute after:bottom-[-10px] after:left-1/2 after:-translate-x-1/2 after:border-[10px] after:border-transparent after:border-t-white/95 after:z-10 after:drop-shadow-md';
      case 'bottom':
        return 'after:content-[""] after:absolute after:top-[-10px] after:left-1/2 after:-translate-x-1/2 after:border-[10px] after:border-transparent after:border-b-white/95 after:z-10 after:drop-shadow-md';
      case 'left':
        return 'after:content-[""] after:absolute after:left-[-10px] after:top-1/2 after:-translate-y-1/2 after:border-[10px] after:border-transparent after:border-r-white/95 after:z-10 after:drop-shadow-md';
      case 'right':
        return 'after:content-[""] after:absolute after:right-[-10px] after:top-1/2 after:-translate-y-1/2 after:border-[10px] after:border-transparent after:border-l-white/95 after:z-10 after:drop-shadow-md';
      default:
        return '';
    }
  };

  return (
    <>
      {/* Backdrop overlay - only visible when no target is specified */}
      {!currentStepData?.target && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-fadeIn"
          onClick={skipTour}
        />
      )}

      {/* Tour Tooltip */}
      <div
        className={`fixed z-50 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl p-5 w-80 transition-all duration-500 border border-purple-200/50 ${getModalPosition()} ${getArrowStyles()} animate-slideInUp`}
        style={{
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(147, 51, 234, 0.1)'
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-1.5 shadow-lg">
              <SparklesIcon className="h-4 w-4 text-white flex-shrink-0" />
            </div>
            <h3 className="text-base font-bold text-gray-900">{currentStepData.title}</h3>
          </div>
          <button
            onClick={() => {
              if (dontShowAgain) {
                hideTourPermanently();
              } else {
                skipTour();
              }
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 hover:scale-110 transform -mt-1 -mr-1 rounded-full p-1 hover:bg-gray-100"
            aria-label="Close tour"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <p className="text-sm text-gray-700 mb-4 leading-relaxed">{currentStepData.content}</p>

        {/* Progress */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-1.5">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep ? 'w-8 bg-gradient-to-r from-purple-500 to-purple-600' :
                  index < currentStep ? 'w-2 bg-purple-300' : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>
          <span className="text-xs font-medium text-gray-500">
            {currentStep + 1} of {tourSteps.length}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              if (dontShowAgain) {
                hideTourPermanently();
              } else {
                skipTour();
              }
            }}
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200 font-medium"
          >
            Skip tour
          </button>
          <div className="flex space-x-2">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow"
            >
              <ChevronLeftIcon className="h-3 w-3 mr-0.5" />
              Back
            </button>
            <button
              onClick={() => {
                if (currentStep === tourSteps.length - 1) {
                  if (dontShowAgain) {
                    hideTourPermanently();
                  } else {
                    endTour();
                  }
                } else {
                  nextStep();
                }
              }}
              className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
            >
              {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
              {currentStep < tourSteps.length - 1 && <ChevronRightIcon className="h-3 w-3 ml-0.5" />}
            </button>
          </div>
        </div>

        {/* Don't show again checkbox - show on all steps */}
        <div className="flex items-center pt-3 mt-3 border-t border-gray-200/50">
          <input
            type="checkbox"
            id="dontShowAgain"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="h-3.5 w-3.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2 transition-all duration-200"
          />
          <label htmlFor="dontShowAgain" className="ml-2 text-xs text-gray-500 cursor-pointer select-none">
            Don't show tour button again
          </label>
        </div>
      </div>
    </>
  );
}